// components/AgendaBar.jsx
import { useEffect, useRef, useCallback, useState } from "react";
import { createPortal } from "react-dom";
import { RichTextEditor } from "@mantine/rte";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPen, faCheck } from "@fortawesome/free-solid-svg-icons";

export default function AgendaBar({
  agenda, setAgenda,
  agendaInput, setAgendaInput,
  showAgendaInput, setShowAgendaInput,
}) {
  const secretId = localStorage.getItem("secretId");
  const wrapperRef = useRef(null);
  const savingRef = useRef(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 767.98);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // --- pobierz agendę przy montażu
  useEffect(() => {
    const stripHtml = (html) =>
      String(html || "").replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ");

    const fetchAgenda = async () => {
      if (!secretId) return;
      try {
        const res = await fetch(`https://api18081113.sayq.pl/get-agenda/${secretId}`);
        const data = await res.json();
        if (res.ok && typeof data?.agenda === "string") {
          setAgenda(data.agenda);
          if (!data.agenda || stripHtml(data.agenda).trim() === "") {
            setAgendaInput("");
            setShowAgendaInput(true);
          }
        } else {
          setAgenda("");
          setAgendaInput("");
          setShowAgendaInput(true);
        }
      } catch (err) {
        console.error("Błąd pobierania agendy:", err);
      }
    };
    fetchAgenda();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [secretId]);

  const stripHtml = (html) =>
    String(html || "").replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ");

  const saveAgenda = useCallback(async () => {
    if (savingRef.current) return;
    const incoming = (agendaInput ?? "").trim();
    const prev = (agenda ?? "").trim();

    // nic się nie zmieniło → tylko zamknij edycję
    if (incoming === prev) {
      setShowAgendaInput(false);
      return;
    }

    savingRef.current = true;
    const normalized = stripHtml(incoming).trim() ? incoming : "";

    try {
      const res = await fetch("https://api18081113.sayq.pl/add-agenda", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ secretId, agenda: normalized }),
      });
      if (!res.ok) throw new Error("HTTP " + res.status);

      setAgenda(normalized);
      setAgendaInput(normalized);
      setShowAgendaInput(false);
    } catch (e) {
      console.error("Błąd zapisu agendy:", e);
      // zostaw w edycji, żeby user nie stracił treści
    } finally {
      savingRef.current = false;
    }
  }, [agendaInput, agenda, secretId, setAgenda, setAgendaInput, setShowAgendaInput]);

  // --- blur wrappera: zapisz, jeśli focus wychodzi poza komponent
  const handleWrapperBlur = (e) => {
    const wrapper = wrapperRef.current;
    const next = e.relatedTarget; // element, który dostaje focus
    if (!wrapper) return;
    if (!wrapper.contains(next)) {
      saveAgenda();
    }
  };

  // --- klik poza komponentem: zapisz i zamknij edycję
  useEffect(() => {
    if (!showAgendaInput || isMobile) return;

    const onDocDown = (ev) => {
      const w = wrapperRef.current;
      if (!w) return;
      if (!w.contains(ev.target)) {
        // kliknięcie poza edytorem → zapis
        saveAgenda();
      }
    };

    document.addEventListener("mousedown", onDocDown);
    document.addEventListener("touchstart", onDocDown, { passive: true });
    return () => {
      document.removeEventListener("mousedown", onDocDown);
      document.removeEventListener("touchstart", onDocDown);
    };
  }, [showAgendaInput, saveAgenda, isMobile]);

  // --- skrót klawiaturowy: Cmd/Ctrl+Enter → zapisz
  const handleKeyDown = (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      saveAgenda();
    }
  };

  const editorContent = (
    <div
      ref={wrapperRef}
      onBlur={handleWrapperBlur}
      onKeyDown={handleKeyDown}
      tabIndex={-1}
      className={`agenda-editor-wrapper agenda-edit-mobile-wrapper h-100 d-flex flex-column ${isMobile ? 'mobile-portal-active' : ''}`}
    >
      <div className="d-md-none bg-white p-3 d-flex justify-content-between align-items-center border-bottom shadow-sm z-3 flex-shrink-0">
        <h5 className="m-0 fw-bold text-dark">Edycja Agendy</h5>
        <button
          onClick={saveAgenda}
          className="btn btn-primary btn-sm rounded-pill fw-bold px-3 shadow-sm"
        >
          <FontAwesomeIcon icon={faCheck} className="me-2" /> Zapisz
        </button>
      </div>
      <div className="flex-grow-1 bg-white position-relative d-flex flex-column">
        <RichTextEditor
          value={agendaInput}
          onChange={setAgendaInput}
          className="flex-grow-1 d-flex flex-column border-0"
          style={{ minHeight: '200px' }}
          controls={[
            ['bold', 'italic', 'underline', 'strike', 'clean'],
            ['h1', 'h2', 'h3', 'h4'],
            ['unorderedList', 'orderedList'],
            ['link', 'blockquote'],
            ['alignLeft', 'alignCenter', 'alignRight'],
          ]}
        />
      </div>
    </div>
  );

  return (
    <div className="agenda-box bg-material halfbox h-100 d-flex flex-column overflow-hidden position-relative">
      <div className="d-flex px-1 justify-content-between align-items-center mb-3 flex-shrink-0">
        <h5 className="fw-bold m-0 text-secondary">Agenda</h5>

        {/* 🔹 przyciski */}
        <div className="d-flex gap-2">
          {!showAgendaInput && (
            <button
              onClick={() => {
                setAgendaInput(agenda || "");
                setShowAgendaInput(true);
              }}
              title="Edytuj agendę"
              className="btn btn-primary rounded-circle d-flex align-items-center justify-content-center shadow-sm"
              style={{ width: 36, height: 36, padding: 0 }}
              type="button"
            >
              <FontAwesomeIcon icon={faPen} />
            </button>
          )}
          {showAgendaInput && (
            <button
              onClick={saveAgenda}
              title="Zapisz agendę"
              className={`btn btn-primary rounded-circle d-flex align-items-center justify-content-center shadow-sm ${isMobile ? 'd-none' : ''}`}
              style={{ width: 36, height: 36, padding: 0 }}
              type="button"
            >
              <FontAwesomeIcon icon={faCheck} />
            </button>
          )}
        </div>
      </div>

      <div className="flex-grow-1 overflow-y-auto nice-scroll p-1 scroll-fade-bottom pb-5">
        {showAgendaInput ? (
          isMobile ? createPortal(editorContent, document.body) : editorContent
        ) : (
          <div className="h-100">
            <div
              className="bg-white p-3 shadow-sm"
              style={{ borderRadius: 12, minHeight: '100%', border: '1px solid #f0f0f0' }}
              onClick={() => {
                setAgendaInput(agenda || "");
                setShowAgendaInput(true);
              }}
              dangerouslySetInnerHTML={{
                __html: agenda && stripHtml(agenda).trim()
                  ? agenda
                  : "<em class='text-muted'>Kliknij ołówek ✏️, aby dodać agendę wydarzenia…</em>",
              }}
            />
          </div>
        )}
      </div>

      <style>{`
        .agenda-editor-wrapper :focus-within {
          outline: 2px solid rgba(99,102,241,.35);
          border-radius: 8px;
        }

        .agenda-edit-mobile-wrapper.mobile-portal-active {
          position: fixed !important;
          top: 0 !important;
          left: 0 !important;
          width: 100vw !important;
          height: 100dvh !important;
          z-index: 99999 !important;
          background: white !important;
        }
        .agenda-edit-mobile-wrapper.mobile-portal-active .quill, 
        .agenda-edit-mobile-wrapper.mobile-portal-active .mantine-RichTextEditor-root {
          display: flex;
          flex-direction: column;
          flex-grow: 1;
          height: 100%;
          border-radius: 0 !important;
          border: none !important;
        }
        .agenda-edit-mobile-wrapper.mobile-portal-active .mantine-RichTextEditor-content {
          flex-grow: 1;
          overflow-y: auto;
        }
      `}</style>
    </div>
  );
}
